import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function TicketsTabModern({
  authHeaders,
  isSuperAdmin = false,
  currentUser = {},
}) {
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [replyText, setReplyText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tickets laden
  const loadTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/tickets`, authHeaders());
      setTickets(res.data || []);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden der Tickets", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Ticket erstellen
  const handleCreateTicket = async () => {
    if (!newTitle.trim() || !newDescription.trim()) {
      toast({ title: "Bitte Titel und Beschreibung ausfüllen", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/tickets`,
        { title: newTitle.trim(), description: newDescription.trim() },
        authHeaders()
      );
      toast({ title: "Ticket erstellt", description: "Der Super-Admin wurde benachrichtigt." });
      setShowCreateDialog(false);
      setNewTitle("");
      setNewDescription("");
      loadTickets();
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Erstellen", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Antwort hinzufügen
  const handleAddReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/tickets/${selectedTicket.id}/reply`,
        { message: replyText.trim() },
        authHeaders()
      );
      toast({ title: "Antwort gesendet" });
      setReplyText("");
      // Ticket neu laden
      const res = await axios.get(`${API_BASE}/api/tickets/${selectedTicket.id}`, authHeaders());
      setSelectedTicket(res.data);
      loadTickets();
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Senden", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status ändern (nur Super-Admin)
  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.patch(
        `${API_BASE}/api/tickets/${ticketId}/status`,
        { status: newStatus },
        authHeaders()
      );
      toast({ title: `Status geändert: ${getStatusLabel(newStatus)}` });
      loadTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Ändern des Status", variant: "destructive" });
    }
  };

  // Ticket löschen (nur Super-Admin)
  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm("Dieses Ticket wirklich löschen?")) return;

    try {
      await axios.delete(`${API_BASE}/api/tickets/${ticketId}`, authHeaders());
      toast({ title: "Ticket gelöscht" });
      setSelectedTicket(null);
      loadTickets();
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Löschen", variant: "destructive" });
    }
  };

  // Status-Hilfsfunktionen
  const getStatusLabel = (status) => {
    switch (status) {
      case "offen": return "Offen";
      case "in_bearbeitung": return "In Bearbeitung";
      case "erledigt": return "Erledigt";
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "offen": return "bg-amber-100 text-amber-800";
      case "in_bearbeitung": return "bg-blue-100 text-blue-800";
      case "erledigt": return "bg-emerald-100 text-emerald-800";
      default: return "bg-slate-100 text-slate-800";
    }
  };

  // Datum formatieren
  const formatDateTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Tickets nach Status gruppieren
  const openTickets = tickets.filter(t => t.status !== "erledigt");
  const closedTickets = tickets.filter(t => t.status === "erledigt");

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Support-Tickets</h2>
          <p className="text-sm text-slate-500">
            {isSuperAdmin ? "Alle gemeldeten Probleme" : "Meine gemeldeten Probleme"}
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-violet-600 hover:bg-violet-500"
          data-testid="create-ticket-btn"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neues Ticket
        </Button>
      </div>

      {/* Offene Tickets */}
      {openTickets.length > 0 && (
        <Card className="border-slate-200 bg-white/90">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">
                {openTickets.length}
              </span>
              Offene Tickets
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {openTickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-3 transition-colors hover:bg-slate-100"
                data-testid={`ticket-item-${ticket.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {getStatusLabel(ticket.status)}
                    </span>
                    <h4 className="font-medium text-slate-900 truncate">{ticket.title}</h4>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {isSuperAdmin && <span className="font-medium">{ticket.created_by_name} · </span>}
                    {formatDateTime(ticket.created_at)}
                    {ticket.replies?.length > 0 && (
                      <span className="ml-2 text-violet-600">
                        💬 {ticket.replies.length} {ticket.replies.length === 1 ? "Antwort" : "Antworten"}
                      </span>
                    )}
                  </p>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Erledigte Tickets */}
      {closedTickets.length > 0 && (
        <Card className="border-slate-200 bg-white/90">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-slate-600">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">
                {closedTickets.length}
              </span>
              Erledigte Tickets
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {closedTickets.slice(0, 5).map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-100 bg-slate-50/50 p-3 transition-colors hover:bg-slate-100"
                data-testid={`ticket-item-${ticket.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {getStatusLabel(ticket.status)}
                    </span>
                    <h4 className="font-medium text-slate-600 truncate">{ticket.title}</h4>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {isSuperAdmin && <span>{ticket.created_by_name} · </span>}
                    {formatDateTime(ticket.created_at)}
                  </p>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            ))}
            {closedTickets.length > 5 && (
              <p className="text-center text-xs text-slate-400">
                ... und {closedTickets.length - 5} weitere erledigte Tickets
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Leerer Zustand */}
      {!loading && tickets.length === 0 && (
        <Card className="border-slate-200 bg-white/90">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-slate-900">Keine Tickets vorhanden</h3>
            <p className="mt-1 text-sm text-slate-500">
              {isSuperAdmin
                ? "Es wurden noch keine Probleme gemeldet."
                : "Du hast noch keine Probleme gemeldet."}
            </p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="mt-4 bg-violet-600 hover:bg-violet-500"
            >
              Erstes Ticket erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Ticket erstellen Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Neues Ticket erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Titel / Kurzbeschreibung</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="z.B. Foto-Upload funktioniert nicht"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                data-testid="ticket-title-input"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Beschreibung des Problems</label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Beschreibe das Problem so genau wie möglich..."
                rows={5}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                data-testid="ticket-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleCreateTicket}
              disabled={isSubmitting || !newTitle.trim() || !newDescription.trim()}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="submit-ticket-btn"
            >
              {isSubmitting ? "Wird gesendet..." : "Ticket erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="text-lg">{selectedTicket.title}</DialogTitle>
                    <p className="mt-1 text-sm text-slate-500">
                      Von {selectedTicket.created_by_name} · {formatDateTime(selectedTicket.created_at)}
                    </p>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(selectedTicket.status)}`}>
                    {getStatusLabel(selectedTicket.status)}
                  </span>
                </div>
              </DialogHeader>

              {/* Beschreibung */}
              <div className="rounded-lg bg-slate-50 p-4 mt-4">
                <div className="text-xs font-medium text-slate-500 mb-2">Problembeschreibung:</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Antworten */}
              {selectedTicket.replies?.length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="text-sm font-medium text-slate-700">Verlauf:</div>
                  {selectedTicket.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className={`rounded-lg p-3 ${
                        reply.is_admin_reply
                          ? "bg-violet-50 border border-violet-100"
                          : "bg-slate-50 border border-slate-100"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${reply.is_admin_reply ? "text-violet-700" : "text-slate-600"}`}>
                          {reply.created_by_name}
                          {reply.is_admin_reply && " (Admin)"}
                        </span>
                        <span className="text-xs text-slate-400">{formatDateTime(reply.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Antwort schreiben */}
              {selectedTicket.status !== "erledigt" && (
                <div className="mt-4 space-y-2">
                  <label className="text-sm font-medium text-slate-700">Antwort schreiben:</label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Deine Nachricht..."
                    rows={3}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                    data-testid="ticket-reply-input"
                  />
                  <Button
                    onClick={handleAddReply}
                    disabled={isSubmitting || !replyText.trim()}
                    className="bg-violet-600 hover:bg-violet-500"
                    data-testid="send-reply-btn"
                  >
                    {isSubmitting ? "Wird gesendet..." : "Antwort senden"}
                  </Button>
                </div>
              )}

              {/* Admin-Aktionen */}
              {isSuperAdmin && (
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-600 mr-2">Status ändern:</span>
                    {["offen", "in_bearbeitung", "erledigt"].map((status) => (
                      <Button
                        key={status}
                        type="button"
                        variant={selectedTicket.status === status ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleStatusChange(selectedTicket.id, status)}
                        className={selectedTicket.status === status ? getStatusColor(status).replace("bg-", "bg-").replace("text-", "text-") : ""}
                      >
                        {getStatusLabel(status)}
                      </Button>
                    ))}
                    <div className="flex-1" />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTicket(selectedTicket.id)}
                      className="text-rose-600 hover:bg-rose-50"
                    >
                      Löschen
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
